/**
 * Market Service (F-272 / E-051)
 *
 * Handles multi-market metadata, Geo-IP auto-detection,
 * market selection persistence (`selected_jurisdiction`),
 * and client-side subdomain redirection for the global hub.
 */

export interface MarketInfo {
  id: 'uk' | 'texas';
  name: string;
  code: string;
  color: string;
  currency: string;
  currencySymbol: string;
  framework: string;
  subdomain: string;
  url: string;
  status: 'ACTIVE' | 'PREVIEW';
  flagEmoji: string;
  description: string;
  features: string[];
}

export const MARKETS: MarketInfo[] = [
  {
    id: 'uk',
    name: 'United Kingdom',
    code: 'UK',
    color: 'blue',
    currency: 'GBP',
    currencySymbol: '£',
    framework: "Housing Act 1988 / Awaab's Law (2024)",
    subdomain: 'uk.landlord.compliance-engine.io',
    url: 'https://uk.landlord.compliance-engine.io',
    status: 'ACTIVE',
    flagEmoji: '🇬🇧',
    description: 'UK Property Compliance, HHSRS, Awaab’s Law & Tenancy Management',
    features: ['HHSRS Risk Assessment', 'Awaab’s Law SLA Tracking', 'PRS Readiness Calculator'],
  },
  {
    id: 'texas',
    name: 'Texas',
    code: 'US-TX',
    color: 'red',
    currency: 'USD',
    currencySymbol: '$',
    framework: 'Texas Property Code / Chapter 92',
    subdomain: 'texas.landlord.compliance-engine.io',
    url: 'https://texas.landlord.compliance-engine.io',
    status: 'ACTIVE',
    flagEmoji: '🇺🇸',
    description: 'Texas Property Code Compliance, 30-Day Security Deposit Tracker & Rekeying Ledger',
    features: ['30-Day Security Deposit Return', '7-Day Rekeying Turnover Ledger', 'Houston & Dallas Rental Registrations'],
  },
];

export const STORAGE_KEY = 'selected_jurisdiction';
export const ALT_STORAGE_KEY = 'selected_market';

export type AppMarket = 'uk' | 'texas' | 'hub';

/**
 * Build-time market selector. `REACT_APP_MARKET` is baked at build time:
 *   - `hub`   → render the global HubLanding (orange-mud SWA)
 *   - `texas` → render the Texas market app (yellow-sand us2-staging)
 *   - unset/`uk` → render the UK market app
 */
export function getAppMarket(): AppMarket {
  const m = process.env.REACT_APP_MARKET;
  if (m === 'hub' || m === 'texas' || m === 'uk') return m as AppMarket;
  return 'uk';
}

export interface StaticJurisdiction {
  id: string;
  name: string;
  state: string;
  abbreviation: string;
}

/**
 * F-266 jurisdiction list, derived from the build-time market.
 *
 * The route the switcher was written against (GET /api/jurisdictions) was never
 * implemented — it 404s on every stage — so the list is served from here rather
 * than firing a request that can only fail. One jurisdiction per market is what
 * a single-market deployment actually has, so the switcher renders it as the
 * static indicator. Replace this with a real API call when the backend ships
 * the route.
 */
export function getJurisdictionsForMarket(market: AppMarket): StaticJurisdiction[] {
  if (market === 'texas') {
    return [{ id: 'tx', name: 'Texas', state: 'Texas', abbreviation: 'TX' }];
  }
  return [{ id: 'uk', name: 'United Kingdom', state: 'England', abbreviation: 'GB' }];
}

export const MarketService = {
  getAppMarket,

  getAvailableMarkets(): MarketInfo[] {
    return MARKETS;
  },

  getMarketInfo(marketId: AppMarket | 'uk' | 'texas'): MarketInfo {
    return (
      MARKETS.find((m) => m.id === marketId) ||
      MARKETS[0]
    );
  },

  getStoredMarket(): 'uk' | 'texas' | null {
    if (typeof window === 'undefined') return null;
    const stored =
      window.localStorage.getItem(STORAGE_KEY) ||
      window.localStorage.getItem(ALT_STORAGE_KEY);
    if (stored === 'texas' || stored === 'uk') {
      return stored;
    }
    return null;
  },

  setStoredMarket(marketId: 'uk' | 'texas'): void {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, marketId);
    window.localStorage.setItem(ALT_STORAGE_KEY, marketId);
  },

  detectGeoMarket(countryCode?: string): 'uk' | 'texas' {
    if (countryCode) {
      const cc = countryCode.toUpperCase().trim();
      if (cc === 'US' || cc === 'USA') return 'texas';
      if (cc === 'GB' || cc === 'UK') return 'uk';
    }

    if (typeof window !== 'undefined') {
      try {
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (
          timeZone.includes('America/Chicago') ||
          timeZone.includes('America/Denver') ||
          timeZone.includes('America/New_York')
        ) {
          return 'texas';
        }
      } catch {
        // ignore fallback errors
      }
    }

    return 'uk';
  },

  getRedirectUrl(marketId: 'uk' | 'texas', currentHost?: string): string {
    const market = MARKETS.find((m) => m.id === marketId) || MARKETS[0];
    const host = currentHost || (typeof window !== 'undefined' ? window.location.hostname : '');

    // In local development or staging without real subdomains, return URL or path
    if (host.includes('localhost') || host.includes('127.0.0.1')) {
      return `/${marketId}`;
    }

    return market.url;
  },

  redirectToMarket(marketId: 'uk' | 'texas', targetUrl?: string): void {
    this.setStoredMarket(marketId);
    const destination = targetUrl || this.getRedirectUrl(marketId);
    if (typeof window !== 'undefined') {
      window.open(destination, '_blank', 'noopener,noreferrer');
    }
  },

  shouldSkipHub(hostname?: string): boolean {
    const host = hostname || (typeof window !== 'undefined' ? window.location.hostname : '');
    if (host.startsWith('uk.') || host.startsWith('texas.')) {
      return true;
    }
    return false;
  },
};

export default MarketService;
